import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (data) => api.post('/auth/login', data),
  adminLogin: (data) => api.post('/auth/admin-login', data),
  partnerLogin: (data) => api.post('/auth/partner-login', data),
  googleCallback: (session_id) => api.post('/auth/google-callback', { session_id }),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
};

export const adminApi = {
  uploadData: (file, overwrite, source = "web_indexing") => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('overwrite', overwrite);
    formData.append('source', source);
    return api.post('/admin/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getPartners: () => api.get('/admin/partners'),
  createPartner: (data) => api.post('/admin/partners', data),
  updatePartner: (id, data) => api.put(`/admin/partners/${id}`, data),
  deletePartner: (id) => api.delete(`/admin/partners/${id}`),
  getCompanies: () => api.get('/admin/companies'),
  createCompany: (data) => api.post('/admin/companies', data),
  updateCompany: (id, data) => api.put(`/admin/companies/${id}`, data),
  deleteCompany: (id) => api.delete(`/admin/companies/${id}`),
  getCampaigns: () => api.get('/admin/campaigns'),
  getPublishers: () => api.get('/admin/publishers'),
  getDashboard: (params) => api.get('/admin/dashboard', { params }),
  getSources: () => api.get('/admin/sources'),
  getAvailableDates: (params) => api.get('/available-dates', { params }),
  exportCsv: (params) => api.get('/admin/export/csv', { params, responseType: 'blob' }),
  getApps: () => api.get('/admin/apps'),
  getAuditLogs: (params) => api.get('/admin/audit/logs', { params }),
  getAuditActions: () => api.get('/admin/audit/actions'),
  getAdCosts: (params) => api.get('/admin/ad-costs', { params }),
  createAdCost: (data) => api.post('/admin/ad-costs', data),
  updateAdCost: (id, data) => api.put(`/admin/ad-costs/${id}`, data),
  deleteAdCost: (id) => api.delete(`/admin/ad-costs/${id}`),
  getContentPartners: () => api.get('/admin/content-partners'),
  createContentPartner: (data) => api.post('/admin/content-partners', data),
  updateContentPartner: (id, data) => api.put(`/admin/content-partners/${id}`, data),
  deleteContentPartner: (id) => api.delete(`/admin/content-partners/${id}`),
  getPeriods: () => api.get('/admin/periods'),
  getPeriodStatement: (month) => api.get(`/admin/periods/${month}`),
  closePeriod: (month) => api.post(`/admin/periods/${month}/close`),
  reopenPeriod: (month) => api.post(`/admin/periods/${month}/reopen`),
  getFxRate: () => api.get('/admin/fx-rate'),
  setFxRate: (reference_usd_inr) => api.put('/admin/fx-rate', { reference_usd_inr }),
  getUnattributedAnalysis: (params) => api.get('/admin/unattributed-analysis', { params }),
  getPreview: (id, params) => api.get(`/admin/preview/${id}`, { params }),
  getProductSettings: () => api.get('/admin/product-settings'),
  setProductSettings: (sandbox_enabled) => api.put('/admin/product-settings', { sandbox_enabled }),
};

export const publisherApi = {
  getChannels: () => api.get('/publisher/channels'),
  getDashboard: (params) => api.get('/publisher/dashboard', { params }),
  exportUrl: '/publisher/dashboard/export',
};

export const partnerApi = {
  getDashboard: (params) => api.get('/partner/dashboard', { params }),
  getAvailableDates: () => api.get('/available-dates'),
};

export const brightApi = {
  getSettings: () => api.get('/admin/bright/settings'),
  updateSettings: (data) => api.put('/admin/bright/settings', data),
  testConnection: () => api.post('/admin/bright/test-connection'),
  getApps: () => api.get('/admin/bright/apps'),
  sync: (data) => api.post('/admin/bright/sync', data),
  getSyncLogs: (limit = 20) => api.get('/admin/bright/sync-logs', { params: { limit } }),
  getDataBrowser: (params) => api.get('/admin/bright/data-browser', { params }),
};

// Product (learning) role — real data with web-indexing money stripped server-side (or the
// synthetic sandbox when the admin enables it). Mirrors adminApi method shapes.
export const productApi = {
  getMode: () => api.get('/product/mode'),
  getDashboard: (params) => api.get('/product/dashboard', { params }),
  getUnattributedAnalysis: (params) => api.get('/product/unattributed-analysis', { params }),
  getCampaigns: () => api.get('/product/campaigns'),
  getPublishers: () => api.get('/product/publishers'),
  getSources: () => api.get('/product/sources'),
  getApps: () => api.get('/product/apps'),
  getAvailableDates: (params) => api.get('/product/available-dates', { params }),
  exportCsv: (params) => api.get('/product/export/csv', { params, responseType: 'blob' }),
  getCompanies: () => api.get('/product/companies'),
  createCompany: (data) => api.post('/product/companies', data),
  updateCompany: (id, data) => api.put(`/product/companies/${id}`, data),
  getPreview: (id, params) => api.get(`/product/preview/${id}`, { params }),
  getAdCosts: () => api.get('/product/ad-costs'),
  getInvoices: () => api.get('/product/billing/invoices'),
  getAuditLogs: (params) => api.get('/product/audit/logs', { params }),
  getAuditActions: () => api.get('/product/audit/actions'),
  getBrightSettings: () => api.get('/product/bright/settings'),
  getBrightSyncLogs: (limit = 20) => api.get('/product/bright/sync-logs', { params: { limit } }),
  getBrightApps: () => api.get('/product/bright/apps'),
  getBrightDataBrowser: (params) => api.get('/product/bright/data-browser', { params }),
  getAniviewSettings: () => api.get('/product/aniview/settings'),
  getAniviewSyncLogs: () => api.get('/product/aniview/sync-logs'),
  getSpringserveSettings: () => api.get('/product/springserve/settings'),
  getSpringserveSyncLogs: () => api.get('/product/springserve/sync-logs'),
};

export default api;